import { PHONE_PARSE_OPTIONS } from "@/lib/constants";
import { WORKSHOP_LANGUAGES } from "@/lib/modules/events/workshops/constants";
import {
  EventAccess,
  EventType,
  SchoolWorkerType,
  State,
} from "@prisma/client";
import parsePhoneNumberFromString from "libphonenumber-js";
import { z } from "zod";

export const CreateWorkshopSchema = z.object({
  title: z.string().trim().min(1, "Name is required."),
  type: z.nativeEnum(EventType),
  eventStartAt: z.date({ required_error: "Date and time are required." }),
  durationMinutes: z.number().int().min(15).max(480),
  description: z.string(),
  ceCreditOfferedForStates: z.array(z.nativeEnum(State)),
  access: z.nativeEnum(EventAccess),
  language: z.enum(WORKSHOP_LANGUAGES),
  ownerUserId: z.number().int().positive(),
  hostIds: z.array(z.number().int().positive()),
  backupHostIds: z.array(z.number().int().positive()).default([]),
  recordingViewableOutsideAdmins: z.boolean(),
});

export const UpdateWorkshopSchema = CreateWorkshopSchema.extend({
  id: z.number().int().positive(),
});

export const ListWorkshopParticipantsInputSchema = z.object({
  eventId: z.number().int().positive(),
});

export type CreateWorkshopInput = z.infer<typeof CreateWorkshopSchema>;
export type UpdateWorkshopInput = z.infer<typeof UpdateWorkshopSchema>;
export type ListWorkshopParticipantsInput = z.infer<
  typeof ListWorkshopParticipantsInputSchema
>;

const optionalUrl = z
  .string()
  .trim()
  .refine(
    (value) => value === "" || z.string().url().safeParse(value).success,
    "Enter a valid photo URL.",
  );

const optionalEmail = z
  .string()
  .trim()
  .refine(
    (value) => value === "" || z.string().email().safeParse(value).success,
    "Enter a valid email.",
  );

export const CreateWorkshopHostSchema = z.object({
  name: z.string().trim().min(1, "Name is required."),
  title: z.string(),
  bio: z.string(),
  photoUrl: optionalUrl,
  email: optionalEmail,
});

export const UpdateWorkshopHostSchema = CreateWorkshopHostSchema.extend({
  id: z.number().int().positive(),
});

export type CreateWorkshopHostInput = z.infer<typeof CreateWorkshopHostSchema>;
export type UpdateWorkshopHostInput = z.infer<typeof UpdateWorkshopHostSchema>;

const registrationName = (label: string) =>
  z.string().trim().min(1, `${label} is required.`).max(64);

const registrationEmail = z
  .string()
  .trim()
  .max(254)
  .email("Enter a valid email.");

const registrationPhone = z.string().trim().max(32).optional();

const requiredRegistrationPhone = z
  .string()
  .trim()
  .min(1, "Phone is required.")
  .max(32)
  .refine(
    (value) =>
      parsePhoneNumberFromString(value, PHONE_PARSE_OPTIONS)?.isValid() ===
      true,
    "Enter a valid phone number.",
  );

const registrationSchoolId = z.number().int().positive().optional();

const registrationSchoolName = z
  .string()
  .trim()
  .max(200)
  .optional()
  .transform((value) => value || undefined);

const registrationRole = z.nativeEnum(SchoolWorkerType, {
  required_error: "Role or title is required.",
});

const registrationHeardAboutWorkshop = z
  .string()
  .trim()
  .min(1, "Tell us where you heard about the workshop.")
  .max(500);

export const RegistrationFormSchema = z.object({
  firstName: registrationName("First name"),
  lastName: registrationName("Last name"),
  email: registrationEmail,
  phone: requiredRegistrationPhone,
  state: z.nativeEnum(State, { required_error: "State is required." }),
  schoolId: registrationSchoolId,
  schoolName: z.string().optional(),
  role: registrationRole,
  heardAboutWorkshop: registrationHeardAboutWorkshop,
});

export const RegisterForWorkshopSchema = z.object({
  eventId: z.number().int().positive(),
  firstName: registrationName("First name"),
  lastName: registrationName("Last name"),
  email: registrationEmail.transform((value) => value.toLowerCase()),
  phone: registrationPhone.transform((value) => value || undefined),
  state: z.nativeEnum(State).optional(),
  schoolId: registrationSchoolId,
  schoolName: registrationSchoolName,
  role: registrationRole,
  heardAboutWorkshop: registrationHeardAboutWorkshop,
  token: z.string().optional(),
});

export type RegistrationFormValues = z.infer<typeof RegistrationFormSchema>;
export type RegisterForWorkshopInput = z.infer<
  typeof RegisterForWorkshopSchema
>;

export const ShareWorkshopFormSchema = z.discriminatedUnion("channel", [
  z.object({
    channel: z.literal("EMAIL"),
    email: registrationEmail,
    phone: z.string(),
    language: z.enum(["ENGLISH", "SPANISH"]),
  }),
  z.object({
    channel: z.literal("TEXT"),
    phone: requiredRegistrationPhone,
    email: z.string(),
    language: z.enum(["ENGLISH", "SPANISH"]),
  }),
]);

export type ShareWorkshopFormValues = z.infer<typeof ShareWorkshopFormSchema>;
